/**
 * "Is this document on its way out?"
 *
 * When the browser cancels an in-flight `fetch` because the page is being
 * navigated away from, frozen, or closed, the request rejects with exactly the
 * same bare `TypeError` ("Failed to fetch" / "Load failed") that a genuine
 * transport failure produces. Nothing on the error distinguishes the two — the
 * only signal is the page's own lifecycle state, which is what this tracks.
 *
 * Used to decide whether a dropped request is worth reporting: a request the
 * browser itself cancelled on the way out has no one left to affect and no code
 * change that would prevent it. See NONRESIDENT-DM.
 */

let unloadStarted = false

if (typeof window !== 'undefined') {
  const markUnloading = () => {
    unloadStarted = true
  }

  // `pagehide` covers a real unload and a bfcache freeze; `beforeunload` fires a
  // little earlier on a same-tab navigation. Either one means the browser is
  // about to cancel whatever is in flight.
  window.addEventListener('pagehide', markUnloading)
  window.addEventListener('beforeunload', markUnloading)
  // A page restored from bfcache is live again, so its failures matter again.
  window.addEventListener('pageshow', () => {
    unloadStarted = false
  })
}

export function isPageTeardownInProgress(): boolean {
  if (unloadStarted) {
    return true
  }

  // A tab that is closing or backgrounded reports `hidden`, and in several
  // browsers that is the ONLY notice given before in-flight requests are
  // cancelled — no unload event we could have listened for ever fires.
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

/**
 * Calls `onVisible` once, the next time the page is visible again: restored
 * from the back/forward cache (`pageshow`) or brought back to the foreground
 * (`visibilitychange`). Returns a function that stops waiting.
 *
 * For work put off because the page looked like it was going away (see
 * `isPageTeardownInProgress`): a hidden tab is not always a closing one, and a
 * frozen page can come back, so the work resumes here if it does. Keyed on
 * visibility alone, because a `beforeunload` that led nowhere leaves the
 * teardown flag set on a page that is still in use.
 */
export function whenPageIsVisibleAgain(onVisible: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const stop = () => {
    window.removeEventListener('pageshow', check)
    document.removeEventListener('visibilitychange', check)
  }
  function check() {
    if (document.visibilityState !== 'visible') {
      return
    }
    stop()
    onVisible()
  }

  window.addEventListener('pageshow', check)
  document.addEventListener('visibilitychange', check)
  return stop
}
